import { type InspectorTurn, type InspectorUsage } from './turn-waterfall';

/**
 * @desc Parse the conversation view's `?turn=` search value: a positive integer (1-based), or
 * undefined for anything else. Accepts the number TanStack's JSON search parser produces and a
 * numeric string (a hand-typed URL). Clamping to the conversation's actual turn count happens at
 * render time, once the turns are known — see `clampTurn`.
 */
export const parseTurnParam = (raw: unknown): number | undefined => {
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
  return Number.isInteger(n) && n >= 1 ? n : undefined;
};

/**
 * @desc The 1-based turn to show: `requested` clamped into `1..count`, defaulting to 1. Zero when
 * there are no turns at all, so callers can branch on it.
 */
export const clampTurn = (requested: number | undefined, count: number): number => {
  if (count <= 0) return 0;
  if (requested === undefined || !Number.isFinite(requested)) return 1;
  return Math.min(count, Math.max(1, Math.trunc(requested)));
};

/** @desc The 1-based position of `turnId` among `turns`, or undefined when it is not one of them. */
export const turnNumberOf = (
  turns: readonly InspectorTurn[],
  turnId: string | null | undefined,
): number | undefined => {
  if (!turnId) return undefined;
  const index = turns.findIndex((t) => t.turnId === turnId);
  return index === -1 ? undefined : index + 1;
};

/**
 * @desc At-a-glance figures for one conversation, derived from the waterfall the view already
 * computed rather than refetched. Mirrors `Conversation.InspectorStats` where the two overlap;
 * `errors` is the reducer's (failed tool calls plus ModelCallFailed markers).
 */
export type ConversationGlance = {
  turnCount: number;
  completedTurnCount: number;
  totalTurnElapsedMs: number;
  avgTurnElapsedMs: number | null;
  modelCalls: number;
  toolCalls: number;
  errors: number;
  turnsWithErrors: number;
  tokens: InspectorUsage & { total: number };
};

export const conversationGlance = (turns: readonly InspectorTurn[]): ConversationGlance => {
  const tokens = { input: 0, output: 0, cacheRead: 0, cacheWrite5m: 0, cacheWrite1h: 0, total: 0 };
  let completed = 0;
  let elapsed = 0;
  let modelCalls = 0;
  let toolCalls = 0;
  let errors = 0;
  let turnsWithErrors = 0;
  for (const { totals } of turns) {
    if (totals.elapsedMs !== undefined) {
      completed += 1;
      elapsed += totals.elapsedMs;
    }
    modelCalls += totals.modelCalls;
    toolCalls += totals.toolCalls;
    errors += totals.errors;
    if (totals.errors > 0) turnsWithErrors += 1;
    tokens.input += totals.usage.input;
    tokens.output += totals.usage.output;
    tokens.cacheRead += totals.usage.cacheRead;
    tokens.cacheWrite5m += totals.usage.cacheWrite5m;
    tokens.cacheWrite1h += totals.usage.cacheWrite1h;
  }
  tokens.total =
    tokens.input + tokens.output + tokens.cacheRead + tokens.cacheWrite5m + tokens.cacheWrite1h;
  return {
    turnCount: turns.length,
    completedTurnCount: completed,
    totalTurnElapsedMs: elapsed,
    avgTurnElapsedMs: completed > 0 ? elapsed / completed : null,
    modelCalls,
    toolCalls,
    errors,
    turnsWithErrors,
    tokens,
  };
};
